import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import compose from 'lodash/fp/compose';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import { PencilFill } from 'react-bootstrap-icons';
import { withTranslation } from 'components/hoc';
import OrgContentTemplate from '../../MyData/common/OrgContentTemplate';
import {
  getFavoriteFormsThunk,
  deleteBulkFavoriteFormsThunk,
} from '../myFormsState/myFormsThunk';

const FillOutBtn = styled.button`
  background: var(--admincat-color-grey-6);
  border-radius: 2px;
  height: 38px;
  color: var(--admincat-color-grey-7);
  border: none;
  font-size: 14px;
  line-height: 10px;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &:not(:disabled):hover {
    background: var(--admincat-color-grey-5);
  }
`;

const filterableColumns = [
  { columnId: 'space', columnDataType: 'text' },
  { columnId: 'description', columnDataType: 'text' },
  { columnId: 'own_sign', columnDataType: 'text' },
  { columnId: 'issuing_org', columnDataType: 'text' },
  { columnId: 'form_category', columnDataType: 'text' },
  { columnId: 'language', columnDataType: 'text' },
  { columnId: 'status', columnDataType: 'text' },
  { columnId: 'pdf_file', columnDataType: 'text' },
  { columnId: 'valid_from', columnDataType: 'date' },
  { columnId: 'valid_to', columnDataType: 'date' },
  { columnId: 'creation_date', columnDataType: 'date' },
];

const FavoriteFormsList = (props) => {
  const { tr } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { favorites, favoritesLoading } = useSelector((state) => state.myForms);

  useEffect(() => {
    dispatch(getFavoriteFormsThunk());
  }, [dispatch]);

  const mapFields = () => {
    return favorites.map(({ id, form_data }) => {
      if (!form_data) return { id };
      return {
        id,
        space: form_data.space_detail?.name || '-',
        description: form_data.description || '-',
        own_sign: form_data.own_sign || '-',
        issuing_org: form_data.issuing_org_detail?.name || '-',
        form_category: form_data.form_category_detail?.long_description_eng || '-',
        language: form_data.language_detail?.long_description_eng || '-',
        status: form_data.status_detail?.id || '-',
        pdf_file: form_data.pdf_file ? form_data.pdf_file.split('/').pop() : '-',
        valid_from: form_data.valid_from || '-',
        valid_to: form_data.valid_to || '-',
        creation_date: form_data.creation_date || '-',
        _form_id: form_data.id,
      };
    });
  };

  const handleBulkDelete = async (ids) => {
    if (!ids || ids.length === 0) {
      Swal.fire(
        tr('Warning!'),
        tr('You have not selected any item to delete'),
        'warning',
      );
      return;
    }
    const confirmed = await Swal.fire({
      title: tr('Are you sure?'),
      text: tr('The selected favorites will be permanently removed.'),
      icon: 'warning',
      buttons: [tr('Cancel'), tr('Delete')],
      dangerMode: true,
    });
    if (confirmed) {
      await dispatch(deleteBulkFavoriteFormsThunk(ids));
      dispatch(getFavoriteFormsThunk());
    }
  };

  const handleFillOut = (rowIds) => {
    if (rowIds.length !== 1) {
      Swal.fire(tr('Info'), tr('Please select exactly one form to fill out.'), 'info');
      return;
    }
    const favorite = favorites.find((f) => f.id === rowIds[0]);
    if (!favorite?.form_data?.id) return;
    navigate(`/dashboard/forms/fill-out?form_id=${favorite.form_data.id}`);
  };

  return (
    <OrgContentTemplate
      tableName="my_forms"
      frontendTableName="my_forms"
      backendTableName="form"
      data={mapFields()}
      toggleForm={() => navigate('/dashboard/my-forms/search')}
      bulkDelete={handleBulkDelete}
      filterableColumns={filterableColumns}
      isLoading={favoritesLoading}
      renderExtraButtons={({ rowIds }) => (
        <FillOutBtn
          disabled={rowIds.length !== 1}
          onClick={() => handleFillOut(rowIds)}
          title={tr('Select exactly one form to fill out')}
        >
          <PencilFill size={13} />
          {tr('Fill-out')}
        </FillOutBtn>
      )}
    />
  );
};

export default compose(withTranslation)(FavoriteFormsList);
